import { useState, type CSSProperties } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DealCategory, DealStage, type DealActivityDto, type DealSummaryDto } from '@bhops/shared';
import { dealsApi } from '@/api/deals';
import { extractApiError } from '@/api/client';
import { Button } from '@/components/Form';
import { useAuthStore } from '@/stores/auth';
import { canEditSales } from '@/permissions';
import { DealActivityCreateModal } from './DealActivityCreateModal';
import { kstYmd } from '@/lib/format-date';
import { DealTransitionModal, isStageTransitionRoleAllowed } from './DealTransitionModal';
import { Icon, type IconName } from '@/components/Icon';
import { StatusPill } from '@/components/StatusPill';

// Deal detail page — design §4.2 + §4.3 surface area: shows the
// Deal's current state, its activities timeline, linked
// contracts, and a stage-event audit log. Operator can record a
// new activity or fire an explicit stage transition from here
// (DnD on the Kanban is the typical path; this page is the
// fallback + the place to inspect history).

const STAGE_LABEL: Record<DealStage, string> = {
  LEAD: '리드',
  QUALIFIED: '검증',
  PROPOSAL: '제안',
  NEGOTIATION: '협상',
  ACTIVE: '계약 체결',
  CHURNED: '이탈',
};

const STAGE_COLOR: Record<DealStage, string> = {
  LEAD: 'var(--fg-muted)',
  QUALIFIED: 'var(--blue-500)',
  PROPOSAL: 'var(--blue-700)',
  NEGOTIATION: 'var(--fg-warning)',
  ACTIVE: 'var(--robot-operating)',
  CHURNED: 'var(--fg-danger)',
};

const CATEGORY_LABEL: Record<DealCategory, string> = {
  NEW: '신규',
  RENEWAL: '재계약',
  EXPANSION: '증설',
};

const ACTIVITY_KIND_ICON: Record<string, IconName> = {
  CALL: 'phone',
  MEETING: 'users',
  EMAIL: 'mail',
  PROPOSAL_SENT: 'file',
  CUSTOM: 'sparkles',
};

// Deal.get response includes activities + contracts + stageEvents
// per the server's `include` shape. The shared DTO doesn't have
// these (they're detail-only) so we widen here.
type DealDetail = DealSummaryDto & {
  activities?: DealActivityDto[];
  contracts?: Array<{
    id: string;
    humanId: string;
    status: string;
    startDate: string;
    endDate: string;
  }>;
  stageEvents?: Array<{
    id: string;
    fromStage: DealStage | null;
    toStage: DealStage;
    reason: string | null;
    actorUserId: string;
    occurredAt: string;
  }>;
};

export function DealDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const userRole = useAuthStore((s) => s.user?.role);
  // 영업 도메인 — canEditSales = ADMIN/SALES_MANAGER/MANAGER. (CASL 동반 수정.)
  const canMutate = !!userRole && canEditSales(userRole);

  const [activityOpen, setActivityOpen] = useState(false);
  const [transitionTo, setTransitionTo] = useState<DealStage | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['deals', id],
    queryFn: () => dealsApi.get(id) as Promise<DealDetail>,
    enabled: id !== '',
  });

  if (isLoading) {
    return (
      <div style={empty} className="kuun-caption">
        불러오는 중…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div style={empty}>
        <span style={{ color: 'var(--fg-danger)' }}>{error ? extractApiError(error) : '없음'}</span>
      </div>
    );
  }

  // round3 #8e — ACTIVE(계약 체결) 직접 전이는 ADMIN 전용(서버 규칙 미러). 비-ADMIN
  // 에게는 '→ 계약 체결' 버튼을 숨겨 422 를 막는다(계약 생성 경로로 유도).
  const validNextStages = (NEXT_STAGES_BY_FROM[data.stage] ?? []).filter((s) =>
    isStageTransitionRoleAllowed(s, userRole),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={headerRow}>
        <Link
          to="/deals"
          className="kuun-caption"
          style={{ color: 'var(--fg-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <Icon name="arrow-left" size={14} aria-hidden /> 영업 파이프라인
        </Link>
        <h1
          className="kuun-h2"
          style={{ margin: '4px 0 0', display: 'flex', gap: 10, alignItems: 'baseline' }}
        >
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--fg-muted)' }}>
            {data.humanId}
          </span>
          <span>{data.customer.displayName}</span>
        </h1>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusPill
            variant="solid"
            tint={STAGE_COLOR[data.stage]}
            label={STAGE_LABEL[data.stage]}
          />
          <StatusPill label={CATEGORY_LABEL[data.category]} />
          <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            담당: {data.salesRepCode}
          </span>
        </div>
      </div>

      {/* Action row */}
      {canMutate && validNextStages.length > 0 && (
        <div style={actionRow}>
          <span className="kuun-label" style={{ color: 'var(--fg-muted)' }}>
            단계 변경:
          </span>
          {validNextStages.map((stage) => (
            <Button
              key={stage}
              type="button"
              variant={stage === DealStage.CHURNED ? 'danger' : 'secondary'}
              onClick={() => setTransitionTo(stage)}
            >
              → {STAGE_LABEL[stage]}
            </Button>
          ))}
        </div>
      )}

      <div style={twoColumn}>
        <section style={panel}>
          <header style={panelHeader}>
            <span>활동 기록</span>
            <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
              {data.activities?.length ?? 0}건
            </span>
            {canMutate && (
              <Button type="button" variant="ghost" onClick={() => setActivityOpen(true)}>
                + 활동
              </Button>
            )}
          </header>
          <div style={panelBody}>
            {(!data.activities || data.activities.length === 0) && (
              <div style={empty} className="kuun-caption">
                활동 기록 없음
              </div>
            )}
            {data.activities?.map((a) => (
              <div key={a.id} style={activityRow}>
                <div aria-hidden style={{ lineHeight: 1, color: 'var(--fg-muted)' }}>
                  <Icon name={ACTIVITY_KIND_ICON[a.kind] ?? 'sparkles'} size={18} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                    {a.kind} · {formatRelative(a.occurredAt)}
                    {a.author && ` · ${a.author.displayName}`}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 2, whiteSpace: 'pre-wrap' }}>
                    {a.notes}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={panel}>
          <header style={panelHeader}>
            <span>연결 계약</span>
            <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
              {data.contracts?.length ?? 0}건
            </span>
            {canMutate &&
              (data.stage === DealStage.NEGOTIATION || data.stage === DealStage.ACTIVE) && (
                // Server allows POST /contracts on any non-deleted Deal
                // stage; ContractCreateWizard's eligibleDeals filter is
                // NEGOTIATION + ACTIVE (the latter covers follow-on
                // contracts — renewals / expansions). Mirror that here
                // so the entry-point matches the wizard's accepted set.
                <Link
                  to={`/contracts/new?dealId=${data.id}`}
                  style={{
                    marginLeft: 'auto',
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--brand)',
                    textDecoration: 'none',
                  }}
                >
                  + 새 계약
                </Link>
              )}
          </header>
          <div style={panelBody}>
            {(!data.contracts || data.contracts.length === 0) && (
              <div style={empty} className="kuun-caption">
                계약 없음
              </div>
            )}
            {data.contracts?.map((c) => (
              <Link
                key={c.id}
                // round4 #5a (BUG#7) — round3 #8f 로 계약 상세가 복구됐으므로 '연결 계약'은
                // 해당 계약 상세로 직행(라인/가격/PDF/해지 확인). 기존 고객사 상세 우회 제거.
                to={`/contracts/${c.id}`}
                style={{
                  ...activityRow,
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{c.humanId}</div>
                  {/* 추가수정 2차(26-07-27) 정정3 — 계약 기간을 KST 로. toYmd(UTC
                      자르기)는 계약 등록 마법사가 한국 자정(=전날 15:00Z)으로 저장한
                      계약일을 하루 이르게 보여줬다. 계약 목록·계약 상세는 이미 KST 라
                      같은 계약이 화면마다 다른 날짜로 읽히던 상태였다. */}
                  <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                    {kstYmd(c.startDate) ?? '-'} ~ {kstYmd(c.endDate) ?? '-'}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 500 }}>{c.status}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <section style={panel}>
        <header style={panelHeader}>
          <span>단계 변경 이력</span>
          <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            {data.stageEvents?.length ?? 0}건
          </span>
        </header>
        <div style={panelBody}>
          {(!data.stageEvents || data.stageEvents.length === 0) && (
            <div style={empty} className="kuun-caption">
              없음
            </div>
          )}
          {data.stageEvents?.map((e) => (
            <div key={e.id} style={activityRow}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13 }}>
                  {e.fromStage ? STAGE_LABEL[e.fromStage] : '(신규)'} →{' '}
                  <strong>{STAGE_LABEL[e.toStage]}</strong>
                </div>
                {e.reason && (
                  <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
                    {e.reason}
                  </div>
                )}
              </div>
              <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                {formatRelative(e.occurredAt)}
              </span>
            </div>
          ))}
        </div>
      </section>

      <DealActivityCreateModal
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        dealId={data.id}
        dealHumanId={data.humanId}
      />

      {transitionTo && (
        <DealTransitionModal
          open={true}
          onClose={() => setTransitionTo(null)}
          deal={data}
          to={transitionTo}
        />
      )}
    </div>
  );
}

// Mirror of the SM transition table — used to render only the
// valid "→ <stage>" buttons. Server is the source of truth.
const NEXT_STAGES_BY_FROM: Record<DealStage, DealStage[]> = {
  LEAD: [DealStage.QUALIFIED, DealStage.CHURNED],
  QUALIFIED: [DealStage.PROPOSAL, DealStage.CHURNED],
  PROPOSAL: [DealStage.NEGOTIATION, DealStage.CHURNED],
  NEGOTIATION: [DealStage.ACTIVE, DealStage.CHURNED],
  ACTIVE: [DealStage.CHURNED],
  CHURNED: [],
};

function formatRelative(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const min = Math.floor(ms / 60_000);
  if (min < 1) return '방금';
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}일 전`;
  // 추가수정 2차(26-07-27) 정정3 — 30일 초과 폴백도 같은 병(UTC 자르기)이었다.
  // 활동/단계이력의 오래된 항목이 하루 이른 날짜로 보였다.
  return kstYmd(iso) ?? '-';
}

const headerRow: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const actionRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 12,
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-sunken)',
  border: '1px solid var(--border-default)',
};

const twoColumn: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
};

const panel: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};

const panelHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 14px',
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 13,
  fontWeight: 600,
};

const panelBody: CSSProperties = {
  padding: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const activityRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '8px 12px',
  borderRadius: 'var(--r-input)',
};

const empty: CSSProperties = {
  padding: 16,
  textAlign: 'center',
};
